import { ToastQueue } from "@react-spectrum/s2";

const COLOR_SCHEME_PREFERENCES = ["system", "light", "dark"];

export const createAppSlice = (set, get) => ({
  isGenerating: false,
  /**
   * Sets the generating state.
   * @param {boolean} value - The new generating state.
   *
   * @returns {void}
   */
  setGenerating: (value) =>
    set({ isGenerating: value }, undefined, "app/setGenerating"),
  isPromptMinimized: true,
  /**
   * Sets the prompt minimized state.
   *
   * @param {boolean} value - The new prompt minimized state.
   *
   * @returns {void}
   */
  setPromptMinimized: (value) =>
    set({ isPromptMinimized: value }, undefined, "app/setPromptMinimized"),
  isExpressDialogOpen: false,
  /**
   * Sets the express dialog open state.
   *
   * @param {boolean} value - The new express dialog open state.
   *
   * @returns {void}
   */
  setExpressDialogOpen: (value) =>
    set({ isExpressDialogOpen: value }, undefined, "app/setExpressDialogOpen"),
  shouldGenerateImages: false,
  /**
   * Sets the should generate images state.
   *
   * @param {boolean} value - The new should generate images state.
   *
   * @returns {void}
   */
  setShouldGenerateImages: (value) =>
    set(
      { shouldGenerateImages: value },
      undefined,
      "app/setShouldGenerateImages",
    ),
  colorScheme: null,
  /**
   * Sets the resolved color scheme ("light" or "dark").
   *
   * @param {string} value - The new color scheme.
   *
   * @returns {void}
   */
  setColorScheme: (value) =>
    set({ colorScheme: value }, undefined, "app/setColorScheme"),
  colorSchemePreference: "system",
  /**
   * Sets the user's color scheme preference and resolves the effective color scheme.
   *
   * @param {"system"|"light"|"dark"} value - The preferred color scheme.
   *
   * @returns {void}
   */
  setColorSchemePreference: (value) => {
    const preference = COLOR_SCHEME_PREFERENCES.includes(value)
      ? value
      : "system";
    const colorScheme =
      preference === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : preference;

    set(
      { colorSchemePreference: preference, colorScheme },
      undefined,
      "app/setColorSchemePreference",
    );
  },
  rolesDisabled: false,
  demoMode: false,
  setDemoMode: (value) =>
    set({ demoMode: !!value }, undefined, "app/setDemoMode"),
  /**
   * Hides the signed-in user's roles in the UI to simulate a regular user.
   *
   * @param {boolean} value - Whether roles should be disabled.
   *
   * @returns {void}
   */
  setRolesDisabled: (value) =>
    set({ rolesDisabled: !!value }, undefined, "app/setRolesDisabled"),
  devModeOverride: null,
  /**
   * Overrides the dev mode flag returned by `useIsDevMode` (admins only).
   *
   * @param {boolean|null} value - Override value, or null to follow NODE_ENV.
   *
   * @returns {void}
   */
  setDevModeOverride: (value) =>
    set(
      { devModeOverride: typeof value === "boolean" ? value : null },
      undefined,
      "app/setDevModeOverride",
    ),
  isMobile: false,
  /**
   * Sets the mobile viewport state.
   *
   * @param {boolean} value - The new mobile viewport state.
   *
   * @returns {void}
   */
  setIsMobile: (value) =>
    set({ isMobile: value }, undefined, "app/setIsMobile"),
  isLargeViewport: false,
  /**
   * Sets the large viewport state.
   *
   * @param {boolean} value - The new large viewport state.
   *
   * @returns {void}
   */
  setIsLargeViewport: (value) =>
    set({ isLargeViewport: value }, undefined, "app/setIsLargeViewport"),
  clearToastRef: null,
  /**
   * Adds a toast message to the queue.
   *
   * @param {string} message - The message to display in the toast.
   * @param {object} options - Options for the toast, such as timeout.
   * @param {string} variant - The variant of the toast (e.g., "neutral", "positive", "negative", "info").
   *
   * @returns {void}
   */
  addToast: (message, options, variant) => {
    // variant can be "neutral", "positive", "negative", "info"
    // console.trace("adding toast:", message, options, variant);

    // Clear the previous toast if it exists
    get().clearToastRef?.();

    if (options?.timeout === -1) {
      options.timeout = 60 * 1000; // 1 minute
    }

    const clearToastRef = ToastQueue[
      variant ?? options?.variant ?? "neutral"
    ]?.(message, options);
    set({ clearToastRef }, undefined, "app/addToast");
  },
  /**
   * Clears the current toast message.
   *
   * @returns {void}
   */
  clearToast: () => {
    const clearToastRef = get().clearToastRef;

    if (clearToastRef) {
      clearToastRef();
      set({ clearToastRef: null }, undefined, "app/clearToast");
    }
  },
  isSideNavCollapsed: true,
  setSideNavCollapsed: (value) =>
    set({ isSideNavCollapsed: value }, undefined, "app/setSideNavCollapsed"),

  imageVariationProgress: {},
  setImageVariationProgress: (variationKey, value) =>
    set(
      (state) => {
        if (typeof value === "function") {
          value = value(state.imageVariationProgress[variationKey]);
        }
        const ret = {
          imageVariationProgress: {
            ...state.imageVariationProgress,
            [variationKey]: value,
          },
        };

        if (value === null) {
          delete ret.imageVariationProgress[variationKey];
        }

        return ret;
      },
      undefined,
      "app/setImageVariationProgress",
    ),
  promptHint: null,
  setPromptHint: (value) =>
    set({ promptHint: value }, undefined, "app/setPromptHint"),
});
